import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import { Tabs, Tab, Box, Divider, Chip, Breadcrumbs, Typography, Stack, Button } from "@mui/material";
import BASE_API_URL from "../../apis/index";
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Carousel from "react-bootstrap/Carousel";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link } from 'react-router-dom';
import CompProductsSectionIngDist from "./AdminCompanyDetails/CompProductsSectionIngDist";
import CompProductsSectionIngManf from "./AdminCompanyDetails/CompProductsSectionIngManf";
import CompServicesSection from "./AdminCompanyDetails/CompServicesSection";
import CompServicesSectionCRO from "./AdminCompanyDetails/CompServicesSectionCRO";
import StatusEditor from "./AdminCompanyDetails/StatusEditor";

export default function AdminCompanyDetails() {
  const [company, setCompany] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [status, setStatus] = useState("Pending");
  const [rejectionReason, setRejectionReason] = useState("");

  const useQuery = () => new URLSearchParams(useLocation().search);
  const compId = useQuery().get("compId");

  useEffect(() => {
    async function fetchCompanyDetails() {
      try {
        const authToken = localStorage.getItem("authToken");
        const res = await axios.get(
          `${BASE_API_URL}/api/admin/companies/${compId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        setCompany(res.data);
        setCompanyTypes(JSON.parse(res.data.company_types || "[]"));
        setStatus(res.data.status);
      } catch (err) {
        console.error("Failed to fetch company data", err);
      }
    }
    fetchCompanyDetails();
  }, [compId]);

  const handleStatusUpdate = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      await axios.patch(
        `${BASE_API_URL}/api/admin/companies/${compId}/status`,
        { status, rejection_reason: status === "Rejected" ? rejectionReason : null },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
    } catch (err) {
      console.error("Failed to update status", err);
      alert("Error updating status");
    }
  };

  if (!company) return <p>Loading...</p>;

  // ✅ Helper to render doc row with button
  const renderDocRow = (label, doc) => (
    <p>
      <strong>{label}:</strong>{" "}
      {doc ? (
        <>
          Uploaded{" "}
          <Button
            variant="outlined"
            size="small"
            sx={{ ml: 2 }}
            onClick={() => window.open(`${BASE_API_URL}/uploads/user_comp_docs/${doc}`, "_blank")}
          >
            View
          </Button>
        </>
      ) : (
        "Not uploaded"
      )}
    </p>
  );

  return (
    <Wrapper className="container">
      <Breadcrumbs aria-label="breadcrumb" className="mb-4 mt-2 pageBreadCrumbs">
        <Link to={"/admin/companies"}> Companies </Link>
        <Typography sx={{ color: "text.primary" }}>
          {company.company_name}
        </Typography>
      </Breadcrumbs>

      <Box className="border rounded p-4">
        <div className="d-flex align-items-center gap-2 mb-4">
          <LeftSide className='d-flex flex-column justify-content-center pe-md-5'>
            <CompLogo>
              <img
                src={`${BASE_API_URL}/uploads/user_comp_docs/${company.company_logo}`}
                alt="Company Logo"
              />
            </CompLogo>
            <Typography variant="h5" className="txt_color_primary">
              {company.company_name}
            </Typography>
            <Box mt={1} mb={1}>
              {companyTypes.map((type) => (
                <Chip
                  key={type}
                  label={type}
                  color="warning"
                  variant="outlined"
                  sx={{ mr: 1 }}
                  className="rounded"
                />
              ))}
            </Box>

            <Typography variant="body2" mt={2} mb={4}>
              GST Number: {company.gst_number}
            </Typography>

            <StatusEditor
              status={status}
              onStatusChange={setStatus}
              rejectionReason={rejectionReason}
              onRejectionReasonChange={setRejectionReason}
              onSubmit={handleStatusUpdate}
            />

          </LeftSide>

          <RightSide>
            {company.company_images && (
              <Carousel>
                {JSON.parse(company.company_images).map((img, idx) => (
                  <Carousel.Item key={idx}>
                    <img
                      className="d-block w-100"
                      src={`${BASE_API_URL}/uploads/user_comp_docs/${img}`}
                      alt={`company-img-${idx}`}
                      style={{
                        maxHeight: "300px",
                        objectFit: "cover",
                        borderRadius: "10px",
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            )}
          </RightSide>
        </div>

        <Tabs value={tabIndex} onChange={(e, val) => setTabIndex(val)} variant="scrollable" className="border-top border-bottom">
          <Tab label="Company Details" />
          <Tab label="Statutory Details" />
          <Tab label="Bank Details" />
          <Tab label="Vendor Details" />
        </Tabs>

        <Divider sx={{ mb: 2 }} />

        {tabIndex === 0 && (
          <Box className="px-4">
            <p> <strong>Address:</strong> {company.address} </p>
            <p> <strong>Country:</strong> {company.country_of_origin} </p>
            <p> <strong>Brief:</strong> {company.brief} </p>
            <Stack
              direction={"row"}
              divider={<Divider orientation="vertical" flexItem />}
              spacing={2}
            >
              <p><strong>Email:</strong>
                {JSON.parse(company.emails).map((itm, idx) => {
                  return <span key={idx} className="rounded px-3 py-1 bg-light border ms-2"> {itm} </span>
                })}
              </p>
              <p><strong>Mobile Numbers:</strong>
                {JSON.parse(company.mobile_numbers).map((num, idx) => {
                  return <span key={idx} className="rounded px-3 py-1 bg-light border ms-2"> {num} </span>
                })}
              </p>
            </Stack>
          </Box>
        )}

        {tabIndex === 1 && (
          <div className="px-4 row">
            <div className="col-md-6">
              <p><strong>PAN Number:</strong> {company.pan_number}</p>
              <p><strong>GST Number:</strong> {company.gst_number}</p>
              <p><strong>CIN Code:</strong> {company.cin_code}</p>
              <p><strong>Annual Turnover:</strong> {company.annual_turnover}</p>
              <p><strong>Vendor Status:</strong> {company.vendor_status}</p>
              <p><strong>Vendor Type:</strong> {company.vendor_type}</p>
            </div>
            <div className="col-md-6">
              {renderDocRow("PAN Document", company.pan_doc)}
              {renderDocRow("GST Document", company.gst_doc)}
              {renderDocRow("CIN Document", company.cin_doc)}
              {renderDocRow("RC Document", company.rc_doc)}
              {renderDocRow("LLP Certificate", company.llp_cert)}
            </div>
          </div>
        )}

        {tabIndex === 2 && (
          <Box className="px-4">
            <p><strong>Account Number:</strong> {company.account_number}</p>
            <p><strong>IFSC:</strong> {company.ifsc}</p>
            <p><strong>Branch:</strong> {company.branch}</p>
            <p><strong>Bank:</strong> {company.bank}</p>
            <p><strong>Account Holder Name:</strong> {company.account_holder_name}</p>
          </Box>
        )}

        {tabIndex === 3 && (
          <Box className="px-4">
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <img
                src={`${BASE_API_URL}/uploads/user_profile_images/${company.profile_image}`}
                alt={company.name}
                className="vendoe_profile_image"
              />
              <Box>
                <Typography><strong>Name:</strong> {company.name}</Typography>
                <Typography><strong>Email:</strong> {company.email}</Typography>
                <Typography><strong>Mobile:</strong> {company.mobile}</Typography>
                <Typography><strong>Company:</strong> {company.company}</Typography>
                <Typography><strong>Designation:</strong> {company.designation}</Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      {company.status === "Approved" && companyTypes.includes("Ingredient Manufacturer") && (
        <CompProductsSectionIngManf compId={company.id} compName={company.company_name} compTypes={company.company_types} />
      )}

      {company.status === "Approved" && companyTypes.includes("Ingredient Distributor") && (
        <CompProductsSectionIngDist compId={company.id} compName={company.company_name} compTypes={company.company_types} />
      )}

      {company.status === "Approved" && companyTypes.includes("Contract Manufacturer") && (
        <CompServicesSection compId={company.id} compName={company.company_name} compTypes={company.company_types} />
      )}

      {company.status === "Approved" && companyTypes.includes("CRO") && (
        <CompServicesSectionCRO compId={company.id} compName={company.company_name} compTypes={company.company_types} />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .txt_color_secondary {
    color: #333;
  }
  .pageBreadCrumbs {
    a:hover {
      text-decoration: none;
      color: var(--secondary-color);
    }
  }
  .vendoe_profile_image{
    width: 150px;
    height: 100px;
    border-radius: 8px;
    object-fit: cover;
  }
`;

const LeftSide = styled(Box)`
  flex: 1;
`;

const RightSide = styled(Box)`
  flex: 1;
`;

const CompLogo = styled.div`
  img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }
`;
